import React, { useState, useEffect } from 'react';
import {
    Box,
    Paper,
    Typography,
    Card,
    CardContent,
    CircularProgress,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Chip
} from '@mui/material';
import {
    AutoGraph,
    Translate,
    Description,
    Face,
    Security,
    Token,
    History,
    People
} from '@mui/icons-material';
import axios from 'axios';

interface UsageSummary {
    total: {
        totalTokens: number;
        promptTokens: number;
        candidatesTokens: number;
    };
    count: number;
    features: Array<{
        feature: string;
        _sum: { totalTokens: number };
        _count: { id: number };
    }>;
    history: Array<{
        date: string;
        totalTokens: number;
        count: number;
    }>;
}

interface UserUsage {
    userId: string | null;
    _sum: {
        totalTokens: number;
        promptTokens: number;
        candidatesTokens: number;
    };
    _count: {
        id: number;
    };
    user: {
        firstName: string;
        lastName: string;
        preferredName?: string;
        email: string;
    };
}

const MetricCard: React.FC<{ title: string; value: string | number; icon: React.ReactNode; color: string; subtitle?: string }> = ({ title, value, icon, color, subtitle }) => (
    <Card elevation={2}>
        <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <Box>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                        {title}
                    </Typography>
                    <Typography variant="h4" component="div" fontWeight="bold">
                        {value}
                    </Typography>
                    {subtitle && (
                        <Typography variant="caption" color="text.secondary">
                            {subtitle}
                        </Typography>
                    )}
                </Box>
                <Box sx={{ bgcolor: color, borderRadius: 2, p: 1.5, color: 'white' }}>
                    {icon}
                </Box>
            </Box>
        </CardContent>
    </Card>
);

const AiUsagePage: React.FC = () => {
    const [summary, setSummary] = useState<UsageSummary | null>(null);
    const [userUsage, setUserUsage] = useState<UserUsage[]>([]);
    const [loading, setLoading] = useState(true);

    const getApiUrl = () => {
        return process.env.NODE_ENV === 'production'
            ? 'https://rideclubnet.com/api'
            : 'http://localhost:3001/api';
    };

    useEffect(() => {
        fetchData();
    }, []);

    const fetchData = async () => {
        try {
            const token = localStorage.getItem('adminToken');
            const [summaryRes, usersRes] = await Promise.all([
                axios.get(`${getApiUrl()}/admin/ai-usage/summary`, {
                    headers: { Authorization: `Bearer ${token}` }
                }),
                axios.get(`${getApiUrl()}/admin/ai-usage/users`, {
                    headers: { Authorization: `Bearer ${token}` }
                })
            ]);
            setSummary(summaryRes.data.data);
            setUserUsage(usersRes.data.data);
        } catch (error) {
            console.error('Failed to fetch AI usage data:', error);
        } finally {
            setLoading(false);
        }
    };

    if (loading || !summary) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
                <CircularProgress />
            </Box>
        );
    }

    const getFeatureIcon = (feature: string) => {
        switch (feature) {
            case 'TRANSLATION': return <Translate />;
            case 'LICENSE_OCR': return <Description />;
            case 'VEHICLE_DOC_OCR': return <Description />;
            case 'INSURANCE_OCR': return <Security />;
            case 'FACE_MATCH': return <Face />;
            case 'CONTENT_MODERATION': return <Security />;
            default: return <AutoGraph />;
        }
    };

    return (
        <Box>
            <Typography variant="h4" gutterBottom fontWeight="bold">
                AI Usage & Tokens Report
            </Typography>
            <Typography variant="body1" color="text.secondary" paragraph>
                Detailed breakdown of AI token consumption across all platform features.
            </Typography>

            <Box sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: '1fr 1fr 1fr 1fr' },
                gap: 3,
                mb: 4
            }}>
                <MetricCard
                    title="Total Tokens"
                    value={(summary.total?.totalTokens || 0).toLocaleString()}
                    icon={<Token />}
                    color="#667eea"
                    subtitle={`${(summary.total?.promptTokens || 0).toLocaleString()} Prompt / ${(summary.total?.candidatesTokens || 0).toLocaleString()} Response`}
                />
                <MetricCard
                    title="Total Requests"
                    value={summary.count.toLocaleString()}
                    icon={<AutoGraph />}
                    color="#764ba2"
                />
                <MetricCard
                    title="Avg Tokens/Req"
                    value={summary.count > 0 ? Math.round((summary.total?.totalTokens || 0) / summary.count).toLocaleString() : 0}
                    icon={<AutoGraph />}
                    color="#f093fb"
                />
                <MetricCard
                    title="Estimated Cost"
                    value={`$${((summary.total?.totalTokens || 0) * 0.000000125).toFixed(2)}`}
                    icon={<Token />}
                    color="#4facfe"
                    subtitle="Based on Gemini 1.5 Flash pricing"
                />
            </Box>

            <Box sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
                gap: 3
            }}>
                <Box>
                    <Paper sx={{ p: 3, mb: 3 }}>
                        <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <History color="primary" /> Usage by Feature
                        </Typography>
                        <TableContainer>
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Feature</TableCell>
                                        <TableCell align="right">Requests</TableCell>
                                        <TableCell align="right">Total Tokens</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {summary.features.map((feature) => (
                                        <TableRow key={feature.feature}>
                                            <TableCell sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                {getFeatureIcon(feature.feature)}
                                                {feature.feature.replace(/_/g, ' ')}
                                            </TableCell>
                                            <TableCell align="right">{feature._count.id.toLocaleString()}</TableCell>
                                            <TableCell align="right">{feature._sum.totalTokens.toLocaleString()}</TableCell>
                                        </TableRow>
                                    ))}
                                    {summary.features.length === 0 && (
                                        <TableRow>
                                            <TableCell colSpan={3} align="center">No data available yet</TableCell>
                                        </TableRow>
                                    )}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Paper>

                    <Paper sx={{ p: 3 }}>
                        <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <History color="primary" /> Daily Token History (Last 30 Days)
                        </Typography>
                        <TableContainer>
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Date</TableCell>
                                        <TableCell align="right">Requests</TableCell>
                                        <TableCell align="right">Tokens</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {summary.history.slice(-7).reverse().map((day) => (
                                        <TableRow key={day.date}>
                                            <TableCell>{day.date}</TableCell>
                                            <TableCell align="right">{day.count}</TableCell>
                                            <TableCell align="right">{day.totalTokens.toLocaleString()}</TableCell>
                                        </TableRow>
                                    ))}
                                    {summary.history.length === 0 && (
                                        <TableRow>
                                            <TableCell colSpan={3} align="center">No data available yet</TableCell>
                                        </TableRow>
                                    )}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Paper>
                </Box>

                <Box>
                    <Paper sx={{ p: 3, height: '100%' }}>
                        <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <People color="primary" /> Top Users (Token Consumption)
                        </Typography>
                        <TableContainer>
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell>User</TableCell>
                                        <TableCell align="right">Requests</TableCell>
                                        <TableCell align="right">Total Tokens</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {userUsage.map((usage, index) => (
                                        <TableRow key={usage.userId || index}>
                                            <TableCell>
                                                <Box>
                                                    <Typography variant="body2">{usage.user.preferredName || usage.user.firstName} {usage.user.lastName ? usage.user.lastName.charAt(0) + '.' : ''}</Typography>
                                                    <Typography variant="caption" color="text.secondary">{usage.user.email}</Typography>
                                                </Box>
                                            </TableCell>
                                            <TableCell align="right">{usage._count.id.toLocaleString()}</TableCell>
                                            <TableCell align="right">
                                                <Chip
                                                    label={usage._sum.totalTokens.toLocaleString()}
                                                    size="small"
                                                    color={index === 0 ? "primary" : "default"}
                                                />
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                    {userUsage.length === 0 && (
                                        <TableRow>
                                            <TableCell colSpan={3} align="center">No data available yet</TableCell>
                                        </TableRow>
                                    )}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Paper>
                </Box>
            </Box>
        </Box>
    );
};

export default AiUsagePage;
